// 의뢰서(workOrders 문서) → 라벨 인쇄용 데이터 변환
// ViewOrder의 보철 그룹핑 로직을 라벨용으로 옮긴 것 (환자명·치식/보철·쉐이드·납기)

const CHART_ORDER = [
    18, 17, 16, 15, 14, 13, 12, 11,
    21, 22, 23, 24, 25, 26, 27, 28,
    48, 47, 46, 45, 44, 43, 42, 41,
    31, 32, 33, 34, 35, 36, 37, 38,
];

const sortTeeth = (teeth) =>
    [...teeth].sort((a, b) => CHART_ORDER.indexOf(a) - CHART_ORDER.indexOf(b));

const parseTeeth = (teeth) =>
    typeof teeth === 'string' ? teeth.split('-').map(Number) : (teeth || []);

export function getProsthesisLines(order) {
    const lines = [];

    const bridgeTeethSet = new Set();
    order.bridgeGroups?.forEach(g => parseTeeth(g.teeth).forEach(t => bridgeTeethSet.add(t)));

    const push = (teethText, typeText, prosthesis, material) => {
        let line = `${teethText} ${typeText ? typeText + ' ' : ''}${prosthesis || '-'}`;
        if (material && material !== '-' && !(prosthesis || '').includes(material)) {
            line += ` (${material})`;
        }
        lines.push(line);
    };

    if ((!order.items || order.items.length === 0) && order.prosthesisGroups?.length > 0) {
        // items가 없는 구형/수집 데이터: prosthesisGroups에서 직접
        order.prosthesisGroups.forEach(g => {
            const teeth = parseTeeth(g.teeth);
            const bridgeT = teeth.filter(t => bridgeTeethSet.has(t));
            const singleT = teeth.filter(t => !bridgeTeethSet.has(t));
            if (bridgeT.length >= 2) push('#' + bridgeT.join(', #'), `브릿지 ${bridgeT.length}본`, g.prosthesis, g.material);
            if (singleT.length > 0) push('#' + singleT.join(', #'), '', g.prosthesis, g.material);
        });
    } else if (order.items?.length > 0) {
        const prosthesisMap = {};
        order.prosthesisGroups?.forEach(g => {
            parseTeeth(g.teeth).forEach(t => prosthesisMap[t] = { prosthesis: g.prosthesis, material: g.material });
        });

        const groups = {};
        order.items.forEach(item => {
            const info = prosthesisMap[item.toothNumber];
            const prosthesis = info?.prosthesis || item.prosthesis || '-';
            const material = info?.material || item.material || '-';
            const key = prosthesis + '|' + material;
            if (!groups[key]) groups[key] = { prosthesis, material, bridge: [], single: [] };
            if (bridgeTeethSet.has(item.toothNumber)) groups[key].bridge.push(item.toothNumber);
            else groups[key].single.push(item.toothNumber);
        });

        Object.values(groups).forEach(g => {
            if (g.single.length > 0) {
                push('#' + sortTeeth(g.single).join(', #'), '', g.prosthesis, g.material);
            }
            if (g.bridge.length > 0) {
                const seen = new Set();
                order.bridgeGroups?.forEach(bg => {
                    const bgTeeth = parseTeeth(bg.teeth);
                    if (bgTeeth.some(t => g.bridge.includes(t))) {
                        const text = '#' + sortTeeth(bgTeeth).join('-#');
                        if (!seen.has(text)) {
                            seen.add(text);
                            push(text, '브릿지', g.prosthesis, g.material);
                        }
                    }
                });
            }
        });
    }

    // 임플란트
    const implantTeeth = new Set();
    order.implantGroups?.forEach(g => {
        const brand = g.brand || '-';
        (g.teeth || []).forEach(t => {
            implantTeeth.add(t);
            lines.push(`#${t} 임플란트 ${brand}`);
        });
    });
    order.items?.forEach(item => {
        const implant = item.implant;
        if (implant && implant !== '' && implant !== '없음' && !implantTeeth.has(item.toothNumber)) {
            lines.push(`#${item.toothNumber} 임플란트 ${implant}`);
        }
    });

    return lines;
}

export function getShadeLabelText(order) {
    if (!order.items || order.items.length === 0) return order.shade || '-';
    const shades = [...new Set(order.items.map(i => i.shade).filter(s => s && s !== '-'))];
    const types = [...new Set(order.items.map(i => i.shadeType).filter(s => s))];
    const shadeText = shades.length > 0 ? shades.join(', ') : '-';
    return shadeText + (types.length > 0 && shadeText !== '-' ? ` (${types.join(', ')})` : '');
}

export function getDueLabelText(order) {
    const d = order.dueDate || order.completionDate;
    if (!d) return '-';
    const date = d.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '-';
    const h = date.getHours();
    const m = date.getMinutes();
    const timeStr = (h > 0 || m > 0) ? ` ${String(h).padStart(2, '0')}시` : '';
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}${timeStr}`;
}

export function buildLabelData(order, clinicName) {
    return {
        patientName: order.patientName,
        chartNumber: order.chartNumber,
        clinicName: order.clinicName || clinicName || '',
        prosthesisLines: getProsthesisLines(order),
        shadeText: getShadeLabelText(order),
        dueText: getDueLabelText(order),
    };
}
